import { SideBarItem } from "@common-components/sidebar/sidebar";
import { Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useAnalyticsService } from '@common-reducers/hooks/use-analytics-service';
import { useSafeLocation } from '@common/services/utils';
import { useRouter } from '@common-reducers/hooks/use-router';
import { cn } from '@/lib/utils';

interface Props {
    to: string;
    /** An MUI icon, or any component that sizes itself from `className`. */
    icon: React.ComponentType<{ className?: string }>;
    /** i18n key of the label (also the tooltip while minimized). */
    titleKey: string;
    active: SideBarItem;
    currentActive?: SideBarItem;
    analyticsAction: string;
    analyticsCategory?: string;
    testId?: string;
    marginTop?: string;
    preserveQueryParams?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    isMinimized?: boolean;
    /** i18n key of a small pill after the label (e.g. "NEW"); hidden while minimized. */
    badgeKey?: string;
    /** A pulsing dot on the icon's corner — the "something new here" hint. */
    iconDot?: boolean;
}

export function SidebarLinkButton({
    to,
    icon: Icon,
    titleKey,
    active,
    currentActive,
    analyticsAction,
    analyticsCategory = 'myAccountSidebar',
    testId,
    marginTop,
    preserveQueryParams = false,
    onClick,
    isMinimized = false,
    badgeKey,
    iconDot = false,
}: Props) {
    const { t } = useTranslation(namespace);
    const analyticsService = useAnalyticsService();
    const location = useSafeLocation();
    const router = useRouter();
    const isActive = active === currentActive;

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (analyticsAction) {
            analyticsService.event(analyticsAction, {
                category: analyticsCategory,
                action: analyticsAction,
            });
        }
        if (onClick) {
            onClick(e);
        }
        const target = preserveQueryParams ? `${to}${location.search}` : to;
        router.navigate(target);
    };

    const translatedTitle = t(titleKey);

    const linkContent = (
        <Link
            to={to}
            className={cn(
                "group relative flex items-center rounded-lg",
                "text-sm font-medium no-underline cursor-pointer",
                "transition-all duration-300 ease-in-out",
                "px-4 py-3 mx-2 gap-3",
                isActive
                    ? "bg-gradient-to-r from-blue-500 to-blue-400 dark:from-blue-600 dark:to-blue-500 text-white shadow-lg shadow-blue-500/20 dark:shadow-blue-600/30"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-blue-600 dark:hover:text-blue-400"
            )}
            style={{ marginTop }}
            data-analytics-action={analyticsAction}
            data-testid={testId}
            onClick={handleClick}
        >
            <div className={cn(
                "relative flex items-center justify-center flex-shrink-0 transition-colors duration-200",
                isActive
                    ? "text-white"
                    : "text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400"
            )}>
                <Icon className="!w-5 !h-5" />
                {iconDot && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-blue-400 animate-pulse" />}
            </div>
            <span
                className={cn(
                    "whitespace-nowrap overflow-hidden",
                    isActive ? "font-semibold" : "",
                )}
                style={{
                    maxWidth: isMinimized ? 0 : 200,
                    opacity: isMinimized ? 0 : 1,
                    transition: 'max-width 0.3s ease, opacity 0.2s ease',
                }}
            >
                {translatedTitle}
            </span>
            {badgeKey && (
                <span
                    className={cn(
                        "px-2 py-0.5 text-[10px] font-semibold rounded-md whitespace-nowrap overflow-hidden",
                        isActive ? "bg-white text-blue-500" : "bg-blue-400 text-white",
                    )}
                    style={{
                        maxWidth: isMinimized ? 0 : 60,
                        opacity: isMinimized ? 0 : 1,
                        padding: isMinimized ? 0 : undefined,
                        marginInlineStart: isMinimized ? 0 : 'auto',
                        transition: 'max-width 0.3s ease, opacity 0.2s ease, padding 0.3s ease, margin 0.3s ease',
                    }}
                >
                    {t(badgeKey)}
                </span>
            )}
        </Link>
    );

    return (
        <Tooltip title={isMinimized ? translatedTitle : ''} placement="right" arrow disableHoverListener={!isMinimized}>
            {linkContent}
        </Tooltip>
    );
}
